import React, { useState, useEffect } from "react";
import { fetchMonitoringAlerts, fetchValidationRealism } from "../../lib/researchApi";
import HealthOverviewSection from "./sections/HealthOverviewSection";
import GovernanceSection from "./sections/GovernanceSection";
import AlertsSection from "./sections/AlertsSection";
import TrendsSection from "./sections/TrendsSection";
import DriftSection from "./sections/DriftSection";
import RankingsSection from "./sections/RankingsSection";
import RegimeSection from "./sections/RegimeSection";
import LifecycleSection from "./sections/LifecycleSection";
import ValidationHistorySection from "./sections/ValidationHistorySection";
import ComparisonSection from "./sections/ComparisonSection";
import RealistWarnings from "./shared/RealistWarnings";
import "./ResearchDashboard.css";

const SECTIONS = [
    { id: "health",     label: "Strategy Health" },
    { id: "governance", label: "Governance" },
    { id: "alerts",     label: "Alerts", alertDot: true },
    { id: "trends",     label: "Trends" },
    { id: "drift",      label: "Drift" },
    { id: "rankings",   label: "Rankings" },
    { id: "regimes",    label: "Regimes" },
    { id: "lifecycle",  label: "Lifecycle" },
    { id: "history",    label: "Validation History" },
    { id: "compare",    label: "Comparison" },
];

function average(values) {
    const nums = values.filter((v) => typeof v === "number" && Number.isFinite(v));
    if (!nums.length) return null;
    return nums.reduce((sum, value) => sum + value, 0) / nums.length;
}

function realismLabel(score, syntheticRatio) {
    if (typeof score === "number" && score >= 0.75) return "High Realism";
    if (typeof score === "number" && score >= 0.45) return "Moderate Realism";
    if (typeof syntheticRatio === "number" && syntheticRatio >= 0.8) return "Synthetic Heavy";
    return "Approximation Heavy";
}

function deploymentImplication(score) {
    if (typeof score !== "number") return null;
    if (score >= 0.75) return "Most validation evidence is grounded in real market behavior.";
    if (score >= 0.45) return "Validation evidence mixes real market behavior with important approximations.";
    return "Validation evidence is approximation-heavy and should reduce deployment confidence.";
}

function aggregateValidationRealism(payload) {
    if (!payload) return null;
    if (payload.realism) return payload.realism;

    const strategies = Object.values(payload.strategies || {});
    if (!strategies.length) return null;

    const combinedWarnings = [
        ...new Set(
            strategies.flatMap((strategyRealism) => strategyRealism?.missing_data_warnings || [])
        ),
    ];
    const mergedFlags = strategies.reduce((acc, strategyRealism) => {
        Object.entries(strategyRealism?.realism_flags || {}).forEach(([key, value]) => {
            if (value) acc[key] = true;
        });
        return acc;
    }, {});
    const providers = [
        ...new Set(
            strategies.flatMap((strategyRealism) => strategyRealism?.provider_ids_used || [])
        ),
    ];

    const realismScore = average(strategies.map((strategyRealism) => strategyRealism?.realism_score));
    const syntheticDependencyRatio = average(strategies.map((strategyRealism) => strategyRealism?.synthetic_dependency_ratio));
    const providerCompletenessScore = average(strategies.map((strategyRealism) => strategyRealism?.provider_completeness_score));
    const historicalDataCoverage = average(strategies.map((strategyRealism) => strategyRealism?.historical_data_coverage));
    const fillRealismScore = average(strategies.map((strategyRealism) => strategyRealism?.fill_realism_score));

    return {
        strategy_count: strategies.length,
        realism_label: realismLabel(realismScore, syntheticDependencyRatio),
        realism_score: realismScore,
        synthetic_dependency_ratio: syntheticDependencyRatio,
        provider_completeness_score: providerCompletenessScore,
        historical_data_coverage: historicalDataCoverage,
        fill_realism_score: fillRealismScore,
        deployment_implication: deploymentImplication(realismScore),
        missing_data_warnings: combinedWarnings,
        provider_ids_used: providers,
        realism_flags: mergedFlags,
    };
}

export default function ResearchDashboard({ user }) {
    const [activeSection, setActiveSection] = useState("health");
    const [criticalAlertCount, setCriticalAlertCount] = useState(0);
    const [realism, setRealism] = useState(null);

    const isAdmin = user?.is_local_admin;

    useEffect(() => {
        if (!isAdmin) return;
        fetchMonitoringAlerts({ severity: "critical", limit: 100 })
            .then(data => setCriticalAlertCount((data.alerts || []).length))
            .catch(() => {});
    }, [isAdmin]);

    useEffect(() => {
        if (!isAdmin) return;
        fetchValidationRealism()
            .then((data) => setRealism(aggregateValidationRealism(data)))
            .catch(() => {});
    }, [isAdmin]);

    if (!isAdmin) {
        return (
            <div className="rd-shell">
                <div className="rd-access-denied">
                    <div className="rd-access-denied-title">Internal Access Only</div>
                    <div>This dashboard requires admin privileges.</div>
                </div>
            </div>
        );
    }

    return (
        <div className="rd-shell">
            {/* Header */}
            <div className="rd-header">
                <div className="rd-title-block">
                    <div className="rd-label">Internal · Research &amp; Governance</div>
                    <h1 className="rd-title">Strategy Validation Dashboard</h1>
                    <div className="rd-subtitle">
                        Governance analysis · Overfit detection · Deployment readiness · Operational confidence
                    </div>
                </div>
                <div className="rd-header-actions">
                    <span className="rd-realism-notice">⚠ Not customer-facing</span>
                </div>
            </div>

            {/* Global realism warnings */}
            {realism && <RealistWarnings realism={realism} />}

            {/* Section navigation */}
            <nav className="rd-section-nav" aria-label="Dashboard sections">
                {SECTIONS.map(sec => (
                    <button
                        key={sec.id}
                        className={`rd-nav-tab ${activeSection === sec.id ? "rd-nav-tab--active" : ""}`}
                        onClick={() => setActiveSection(sec.id)}
                        type="button"
                    >
                        {sec.label}
                        {sec.alertDot && criticalAlertCount > 0 && (
                            <span className="rd-alert-dot" title={`${criticalAlertCount} critical alert${criticalAlertCount !== 1 ? "s" : ""}`} />
                        )}
                    </button>
                ))}
            </nav>

            {/* Section content */}
            <div className="rd-section" key={activeSection}>
                {activeSection === "health" && <HealthOverviewSection />}
                {activeSection === "governance" && <GovernanceSection />}
                {activeSection === "alerts" && <AlertsSection />}
                {activeSection === "trends" && <TrendsSection />}
                {activeSection === "drift" && <DriftSection />}
                {activeSection === "rankings" && <RankingsSection />}
                {activeSection === "regimes" && <RegimeSection />}
                {activeSection === "lifecycle" && <LifecycleSection />}
                {activeSection === "history" && (
                    <ValidationHistorySection
                        onSelectRun={(runId) => {
                            // future: open run detail panel
                            console.info("Selected run:", runId);
                        }}
                    />
                )}
                {activeSection === "compare" && <ComparisonSection />}
            </div>
        </div>
    );
}
